import { useEffect, useRef, useState } from 'react';
import type { ChatSettings, ResponseLength, VocabLevel } from '@arena/shared';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: ChatSettings;
  onChange: (next: ChatSettings) => void;
  /** Current display name for the worldview (may be undefined if using heading fallback). */
  name?: string;
  /** Called when the user commits a new name. Empty string means "clear the override". */
  onRename?: (name: string) => void;
  /** Called when the user clicks "Export as .md". */
  onExport?: () => void;
}

const LENGTH_OPTIONS: { value: ResponseLength; label: string; description: string }[] = [
  { value: 'short', label: 'Short', description: '1–2 sentences. Quick takes only.' },
  { value: 'medium', label: 'Medium', description: 'A paragraph or two. Conversational.' },
  { value: 'long', label: 'Long', description: 'Full depth. Multiple paragraphs.' },
];

const VOCAB_OPTIONS: {
  value: VocabLevel;
  label: string;
  description: string;
  example: string;
}[] = [
  {
    value: 'simple',
    label: 'Simple',
    description: 'Plain words, short sentences — like explaining to a curious 12-year-old.',
    example: '"Climate change makes the planet hotter, which causes bigger storms."',
  },
  {
    value: 'everyday',
    label: 'Everyday',
    description: 'Clear and direct. Jargon only when needed, always explained.',
    example: '"Rising CO₂ levels are driving global temperatures up year over year."',
  },
  {
    value: 'sophisticated',
    label: 'Sophisticated',
    description: 'Precise and nuanced. Assumes an educated, attentive reader.',
    example: '"Anthropogenic forcing is progressively amplifying the natural greenhouse effect."',
  },
  {
    value: 'academic',
    label: 'Academic',
    description: 'Technical, field-specific vocabulary. Graduate-level discourse.',
    example:
      '"Radiative forcing from elevated GHG concentrations is perturbing the Earth\'s energy budget."',
  },
];

export function SettingsModal({ open, onClose, settings, onChange, name, onRename, onExport }: SettingsModalProps) {
  const [draftName, setDraftName] = useState(name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync draft when the modal opens or the external name changes
  useEffect(() => {
    setDraftName(name ?? '');
  }, [open, name]);

  const commitRename = () => {
    if (onRename) onRename(draftName);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="settings-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Chat settings"
    >
      <div
        className="settings-modal__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="settings-modal__panel">
        <button
          type="button"
          className="settings-modal__close"
          onClick={onClose}
          aria-label="Close settings"
        >
          ×
        </button>

        {onRename && (
          <div className="settings-modal__rename">
            <label className="settings-modal__rename-label" htmlFor="worldview-name-input">
              Name
            </label>
            <input
              id="worldview-name-input"
              ref={inputRef}
              className="settings-modal__rename-input"
              type="text"
              value={draftName}
              placeholder="e.g. Arthur's Brain"
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitRename();
                  inputRef.current?.blur();
                }
              }}
              maxLength={80}
            />
          </div>
        )}

        <p className="settings-modal__eyebrow">— Preferences —</p>
        <h2 className="settings-modal__title">Chat Settings</h2>
        <p className="settings-modal__subtitle">
          Control how your worldview speaks to you.
        </p>

        {/* ── Response length ── */}
        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">Response length</h3>
          <div className="settings-modal__pills" role="group" aria-label="Response length">
            {LENGTH_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`settings-modal__pill${settings.responseLength === opt.value ? ' settings-modal__pill--active' : ''}`}
                onClick={() => onChange({ ...settings, responseLength: opt.value })}
                aria-pressed={settings.responseLength === opt.value}
              >
                <span className="settings-modal__pill-label">{opt.label}</span>
                <span className="settings-modal__pill-desc">{opt.description}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ── Vocabulary level ── */}
        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">Vocabulary level</h3>
          <div className="settings-modal__vocab-grid" role="group" aria-label="Vocabulary level">
            {VOCAB_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`settings-modal__vocab-card${settings.vocabLevel === opt.value ? ' settings-modal__vocab-card--active' : ''}`}
                onClick={() => onChange({ ...settings, vocabLevel: opt.value })}
                aria-pressed={settings.vocabLevel === opt.value}
              >
                <span className="settings-modal__vocab-label">{opt.label}</span>
                <span className="settings-modal__vocab-desc">{opt.description}</span>
                <span className="settings-modal__vocab-example">{opt.example}</span>
              </button>
            ))}
          </div>
        </section>

        <div className="settings-modal__footer">
          <button
            type="button"
            className="settings-modal__done"
            onClick={onClose}
          >
            Done
          </button>
          {onExport && (
            <button
              type="button"
              className="settings-modal__export"
              onClick={() => { onExport(); onClose(); }}
            >
              Export as .md
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
